/**
 * FullPageLoadingOverlay — a blocking, full-viewport overlay for long-running
 * operations (AI image/video generation, large uploads, template generation,
 * publishing). Unlike an inline spinner it sits ABOVE the entire app — sidebar,
 * header, dialogs, drawers, snackbars and menus — so a multi-minute task can
 * never look frozen and the UI behind it can never be clicked.
 *
 * Built on MUI <Modal> so we inherit, for free and correctly:
 *   - focus trap + focus restore on close,
 *   - aria-modal semantics,
 *   - interaction blocking (pointer + inert background),
 *   - Escape disabled and no backdrop-dismiss (there is no onClose).
 *
 * It never shows a fake percentage — only an indeterminate loader plus optional
 * rotating status captions the caller supplies. Respects reduced-motion.
 *
 *   <FullPageLoadingOverlay
 *     open={isGeneratingImage}
 *     title="Generating your image"
 *     message="This may take up to a few minutes. Please keep this page open."
 *     statuses={["Sending request to AI…", "Generating image…", "Saving…"]}
 *   />
 */
import React, { useEffect, useState } from "react";
import {
  Modal, Box, Typography, CircularProgress, useMediaQuery,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  NAVY, NAVY_DARK, GOLD, CARD_BG, TEXT_SECONDARY,
} from "../../theme/tokens";

export interface FullPageLoadingOverlayProps {
  open: boolean;
  title?: string;
  message?: string;
  /**
   * Optional captions cycled every `statusIntervalMs` while open. These are
   * cosmetic and MUST NOT imply real backend progress — no percentages.
   */
  statuses?: string[];
  statusIntervalMs?: number;
}

// Above every default MUI layer (modal 1300, snackbar 1400, tooltip 1500) and
// any app dialog. Deliberately large so nothing can accidentally sit over it.
const OVERLAY_Z = 20000;

const FullPageLoadingOverlay: React.FC<FullPageLoadingOverlayProps> = ({
  open,
  title = "Working…",
  message = "This may take a little while. Please keep this page open.",
  statuses,
  statusIntervalMs = 6000,
}) => {
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [statusIndex, setStatusIndex] = useState(0);

  // Cycle status captions while open. Text changes are content, not motion, so
  // they run even under reduced-motion (aria-live announces them politely).
  useEffect(() => {
    if (!open || !statuses || statuses.length <= 1) {
      setStatusIndex(0);
      return;
    }
    const id = setInterval(
      () => setStatusIndex((i) => (i + 1) % statuses.length),
      Math.max(1500, statusIntervalMs),
    );
    return () => clearInterval(id);
  }, [open, statuses, statusIntervalMs]);

  const caption = statuses && statuses.length > 0 ? statuses[statusIndex] : null;

  return (
    <Modal
      open={open}
      // No onClose + disableEscapeKeyDown => cannot be dismissed by the user
      // while the operation is in flight.
      disableEscapeKeyDown
      aria-labelledby="fp-overlay-title"
      aria-describedby="fp-overlay-message"
      sx={{ zIndex: OVERLAY_Z, display: "flex", alignItems: "center", justifyContent: "center" }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(7,21,33,0.74)", // NAVY_DARK, semi-transparent
            backdropFilter: "blur(3px)",
          },
        },
      }}
    >
      <Box
        // A blocking modal loader: dialog semantics + a polite live region so
        // the title and rotating status captions are announced without stealing
        // focus. Labelled/described by the title + message below.
        role="dialog"
        aria-modal="true"
        aria-live="polite"
        aria-busy="true"
        tabIndex={-1}
        sx={{
          outline: "none",
          width: "min(92vw, 420px)",
          bgcolor: CARD_BG,
          borderRadius: 3,
          boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
          border: `1px solid ${GOLD}`,
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 5 },
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* Loader: gold ring around a sparkle. Indeterminate — never fake %. */}
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <CircularProgress
            {...(reduceMotion
              ? { variant: "determinate" as const, value: 75 }
              : { variant: "indeterminate" as const })}
            size={72}
            thickness={3.5}
            sx={{ color: GOLD }}
            aria-label="Loading"
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: NAVY,
              ...(reduceMotion
                ? {}
                : {
                    animation: "fpSparkle 1.8s ease-in-out infinite",
                    "@keyframes fpSparkle": {
                      "0%, 100%": { opacity: 0.55, transform: "scale(0.9)" },
                      "50%": { opacity: 1, transform: "scale(1.08)" },
                    },
                  }),
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 30, color: GOLD }} />
          </Box>
        </Box>

        <Typography
          id="fp-overlay-title"
          sx={{ fontSize: 20, fontWeight: 700, color: NAVY, lineHeight: 1.3 }}
        >
          {title}
        </Typography>

        <Typography
          id="fp-overlay-message"
          sx={{ fontSize: 14, color: TEXT_SECONDARY, maxWidth: 320 }}
        >
          {message}
        </Typography>

        {caption && (
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: GOLD,
              mt: 0.5,
              letterSpacing: "0.01em",
              // Subtle background chip for legibility on the card.
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              bgcolor: `${NAVY_DARK}0D`,
            }}
          >
            {caption}
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default FullPageLoadingOverlay;
